import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { GenericRequestDto } from "../models/generic-request.dto";
import { GenericResponseDto } from "../models/generic-response.dto";
import { GetAllBuyersResponseDto } from "../models/orders/getAllBuyersResponseDto";
import { FileUploadDto } from "../models/shared/fileUploadDto";
import { AddTaskNotes } from "../models/task-management/addTaskNotesDto";
import { AddTaskOrder } from "../models/task-management/addTaskOrderDto";
import { AddTaskRequestDto } from "../models/task-management/addTaskRequestDto";
import { AssigneeDto } from "../models/task-management/assigneeDto";
import { createCommentDto } from "../models/task-management/createCommentDto";
import { GetActivityLogsDto } from "../models/task-management/getActivityLogsDto";
import { GetAllAssignedUsers } from "../models/task-management/getAllAssignedUsersDto";
import { GetAllTaskResponse } from "../models/task-management/getAllTaskResponseDto";
import { GetCollaboratersDto } from "../models/task-management/getCollaboratersDto";
import { CommentListDto } from "../models/task-management/getCommentList";
import { GetLinkedProjectsDto } from "../models/task-management/getLinkedProjectsDto";
import { OwnersByProject } from "../models/task-management/getOwnersByProject";
import { ProjectWorkflowDetail } from "../models/task-management/getProjectWorkFlowDetailDto";
import { ProjectWorkFlowLinesList } from "../models/task-management/getProjectWorkFlowLinesList";
import { GetReminderListDto } from "../models/task-management/getReminderListDto";
import { ProjectWorkflowTags } from "../models/task-management/getTagsByProjectWorkflowList";
import { GetTaskDefaultViewSettingDto } from "../models/task-management/getTaskDefaultViewSetting";
import { GetTaskListDto } from "../models/task-management/getTaskListDto";
import { GetTaskLogDto } from "../models/task-management/getTaskLogDto";
import { GetTaskSwimLanesDto } from "../models/task-management/getTaskSwimLanesDto";
import { GetUserTodoListDto } from "../models/task-management/getUserTodoListDto";
import { UpdateNotificationSetting } from "../models/task-management/updateNotificationSettingDto";
import { UpdateTaskPriority } from "../models/task-management/updatePriorityDto";
import { UpdateTaskRequestDto } from "../models/task-management/updateTaskDto";
import { UpdateTaskLine } from "../models/task-management/updateTaskLineDto";
import { getTaskDetailResponse } from "../models/task-management/getTaskDetailResponse";
import { SalesOrderLookupDto } from "../models/task-management/salesOrderLookupDto";
import { GetOverdueTasksRequestDto } from "../models/task-management/getOverdueTasksRequestDto";

@Injectable({
  providedIn: "root",
})
export class TaskManagementService {
  private taskManagementBaseURL = environment.baseUrl + "/TaskManagement";
  private fileManagementBaseURL = environment.baseUrl + "/fileManagement";

  constructor(private _http: HttpClient) {}

  getUserTodoList(): Observable<GenericResponseDto<GetUserTodoListDto[]>> {
    return this._http.get<GenericResponseDto<GetUserTodoListDto[]>>(
      this.taskManagementBaseURL + `/GetUserTodoList`
    );
  }

  deleteUserTodoItemById(
    id: number
  ): Observable<GenericResponseDto<GetReminderListDto>> {
    return this._http.post<GenericResponseDto<GetReminderListDto>>(
      this.taskManagementBaseURL + `/DeleteUserTodoItem?Id=${id}`,
      id
    );
  }

  getUserTodaysTaskList(): Observable<GenericResponseDto<GetTaskListDto[]>> {
    return this._http.get<GenericResponseDto<GetTaskListDto[]>>(
      this.taskManagementBaseURL + `/GetTodayTasks`
    );
  }

  getCollaboratersList(): Observable<
    GenericResponseDto<GetCollaboratersDto[]>
  > {
    return this._http.get<GenericResponseDto<GetCollaboratersDto[]>>(
      this.taskManagementBaseURL + `/GetCollaborators`
    );
  }

  getActivityLogs(): Observable<GenericResponseDto<GetActivityLogsDto[]>> {
    return this._http.get<GenericResponseDto<GetActivityLogsDto[]>>(
      this.taskManagementBaseURL + `/GetActivityLogs`
    );
  }

  getLinkedProjects(): Observable<GenericResponseDto<GetLinkedProjectsDto[]>> {
    return this._http.get<GenericResponseDto<GetLinkedProjectsDto[]>>(
      this.taskManagementBaseURL + `/GetLinkedProjects`
    );
  }

  getReminderList(): Observable<GenericResponseDto<GetReminderListDto[]>> {
    return this._http.get<GenericResponseDto<GetReminderListDto[]>>(
      this.taskManagementBaseURL + `/GetReminderList`
    );
  }

  deleteUserReminderById(
    id: number
  ): Observable<GenericResponseDto<GetReminderListDto>> {
    return this._http.post<GenericResponseDto<GetReminderListDto>>(
      this.taskManagementBaseURL + `/DeleteReminder?Id=${id}`,
      id
    );
  }

  getUserUpcomingTaskList(data: {
    pageSize: number;
    pageNumber: number;
  }): Observable<GenericResponseDto<GetTaskListDto[]>> {
    const params = new HttpParams({ fromObject: data });
    return this._http.get<GenericResponseDto<GetTaskListDto[]>>(
      this.taskManagementBaseURL + `/GetUpcomingTasks`,
      { params }
    );
  }

  getOverdueTasks(
    payload: GetOverdueTasksRequestDto
  ): Observable<GenericResponseDto<GetTaskListDto[]>> {
    return this._http.post<GenericResponseDto<GetTaskListDto[]>>(
      this.taskManagementBaseURL + `/GetOverdueTasks`,
      payload
    );
  }

  addorUpdateUserTodoData(
    payload: GetUserTodoListDto
  ): Observable<GenericResponseDto<GetUserTodoListDto>> {
    return this._http.post<GenericResponseDto<GetUserTodoListDto>>(
      this.taskManagementBaseURL + "/PostCreateUserTodoItem",
      payload
    );
  }

  addReminderData(
    payload: GetUserTodoListDto
  ): Observable<GenericResponseDto<GetReminderListDto>> {
    return this._http.post<GenericResponseDto<GetReminderListDto>>(
      this.taskManagementBaseURL + "/PostCreateReminder",
      payload
    );
  }

  addorUpdateSwimLanes(
    payload: GetTaskSwimLanesDto
  ): Observable<GenericResponseDto<GetTaskSwimLanesDto>> {
    return this._http.post<GenericResponseDto<GetTaskSwimLanesDto>>(
      this.taskManagementBaseURL + "/AddUpdateSiwmLaneItem",
      payload
    );
  }

  updateDefaultViewSetting(
    payload: GetTaskDefaultViewSettingDto
  ): Observable<GenericResponseDto<GetTaskDefaultViewSettingDto>> {
    return this._http.post<GenericResponseDto<GetTaskDefaultViewSettingDto>>(
      this.taskManagementBaseURL + "/UpdateDefaultViewSettings",
      payload
    );
  }

  getProjectWorkflowLinesList(
    payload: number
  ): Observable<GenericResponseDto<ProjectWorkFlowLinesList[]>> {
    return this._http.get<GenericResponseDto<ProjectWorkFlowLinesList[]>>(
      this.taskManagementBaseURL +
        `/GetProjectWorkflowLinesList?ProjectWorkflowId=${payload}`
    );
  }

  getProjectDetails(): Observable<GenericResponseDto<ProjectWorkflowDetail[]>> {
    return this._http.get<GenericResponseDto<ProjectWorkflowDetail[]>>(
      this.taskManagementBaseURL + `/GetWorkflowProjectDetails`
    );
  }

  getProjectDetailsPaginated(request: {
    searchText: string;
    skip: number;
    take: number;
  }): Observable<GenericResponseDto<ProjectWorkflowDetail[]>> {
    return this._http.post<GenericResponseDto<ProjectWorkflowDetail[]>>(
      this.taskManagementBaseURL + `/GetWorkflowProjectDetails`,
      request
    );
  }

  getOwnersByProject(
    workFlowId: number
  ): Observable<GenericResponseDto<OwnersByProject[]>> {
    return this._http.get<GenericResponseDto<OwnersByProject[]>>(
      this.taskManagementBaseURL +
        `/GetOwnersByProject?ProjectWorkflowId=${workFlowId}`
    );
  }

  getTagsByProjectWorkflowList(): Observable<
    GenericResponseDto<ProjectWorkflowTags[]>
  > {
    return this._http.get<GenericResponseDto<ProjectWorkflowTags[]>>(
      this.taskManagementBaseURL + `/GetTagsByProjectWorkflowList`
    );
  }

  addTask(payload: AddTaskRequestDto): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + "/PostCreateTask",
      payload
    );
  }

  updateTask(
    payload: UpdateTaskRequestDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + "/UpdateTask",
      payload
    );
  }

  getAllProjectWorkFlowTaskList(
    payload: GenericRequestDto
  ): Observable<GenericResponseDto<GetAllTaskResponse[]>> {
    return this._http.post<GenericResponseDto<GetAllTaskResponse[]>>(
      this.taskManagementBaseURL + `/GetTasksList`,
      payload
    );
  }

  getTaskDetails(
    params: number
  ): Observable<GenericResponseDto<getTaskDetailResponse>> {
    return this._http.get<GenericResponseDto<getTaskDetailResponse>>(
      this.taskManagementBaseURL + `/GetTaskDetails?TaskId=${params}`
    );
  }

  uploadProjectFiles(
    fileObj: FileUploadDto[]
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.fileManagementBaseURL + "/upload",
      fileObj[0]
    );
  }

  updateStatus(
    taskId: number,
    status: number
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL +
        `/UpdateTaskStatus?TaskId=${taskId}&Status=${status}`
    );
  }

  followTask(
    taskId: number,
    status: boolean
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL +
        `/FollowTask?TaskId=${taskId}&IsFollow=${status}`
    );
  }

  getTaskLogs(taskId: number): Observable<GenericResponseDto<GetTaskLogDto[]>> {
    return this._http.get<GenericResponseDto<GetTaskLogDto[]>>(
      this.taskManagementBaseURL + `/GetTaskLogs?TaskId=${taskId}`
    );
  }

  getCommentsByTask(
    taskId: number,
    projectWorkflowId: number
  ): Observable<GenericResponseDto<CommentListDto[]>> {
    return this._http.get<GenericResponseDto<CommentListDto[]>>(
      this.taskManagementBaseURL +
        `/GetCommentsByTask?TaskId=${taskId}&ProjectWorkflowId=${projectWorkflowId}`
    );
  }

  addCommentinTask(
    payload: createCommentDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + `/PostCreateComment`,
      payload
    );
  }

  deleteTag(
    taskId: number,
    tagId: number
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL +
        `/UnAssignTaskTag?TaskId=${taskId}&TagId=${tagId}`
    );
  }

  deleteDocumentFromTask(
    taskId: number,
    documentId: number
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL +
        `/DeleteDocument?TaskId=${taskId}&DocumentId=${documentId}`
    );
  }

  updateTaskPrioirty(
    payload: UpdateTaskPriority
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + `/UpdateTaskPriority`,
      payload
    );
  }

  addTaskNotes(payload: AddTaskNotes): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + `/UpdateTaskNotes`,
      payload
    );
  }

  getAllTags(): Observable<GenericResponseDto<ProjectWorkflowTags[]>> {
    return this._http.get<GenericResponseDto<ProjectWorkflowTags[]>>(
      this.taskManagementBaseURL + `/GetAllTags`
    );
  }

  getAllAssignedToUsers(): Observable<
    GenericResponseDto<GetAllAssignedUsers[]>
  > {
    return this._http.get<GenericResponseDto<GetAllAssignedUsers[]>>(
      this.taskManagementBaseURL + `/GetAllAssignedUsers`
    );
  }

  updateTaskOrder(
    payload: AddTaskOrder[]
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + `/AddTaskOrder`,
      payload
    );
  }

  getSwimLanes(): Observable<GenericResponseDto<GetTaskSwimLanesDto[]>> {
    return this._http.get<GenericResponseDto<GetTaskSwimLanesDto[]>>(
      this.taskManagementBaseURL + `/GetSwimLanes`
    );
  }

  getDefaultViewSetting(): Observable<
    GenericResponseDto<GetTaskDefaultViewSettingDto>
  > {
    return this._http.get<GenericResponseDto<GetTaskDefaultViewSettingDto>>(
      this.taskManagementBaseURL + `/GetDefualtTaskViewSettings`
    );
  }

  isAuthorizedToView(taskId: string): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + `/IsAuthorizedToView?TaskId=${taskId}`
    );
  }

  deletePTag(tagId: number): Observable<GenericResponseDto<boolean>> {
    return this._http.get<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + `/DeleteTag?TagId=${tagId}`
    );
  }

  UpdateTaskLine(
    payload: UpdateTaskLine
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + `/UpdateTaskLine`,
      payload
    );
  }

  getDefaultAssignees(): Observable<GenericResponseDto<AssigneeDto[]>> {
    return this._http.get<GenericResponseDto<AssigneeDto[]>>(
      this.taskManagementBaseURL + `/GetAuthorizedUsers`
    );
  }

  getTeamUsers(): Observable<GenericResponseDto<AssigneeDto[]>> {
    return this._http.get<GenericResponseDto<AssigneeDto[]>>(
      this.taskManagementBaseURL + `/GetTeamUsers`
    );
  }

  updateNotificationSetting(
    payload: UpdateNotificationSetting
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + `/UpdateTaskNotifications`,
      payload
    );
  }

  getAllBuyers(request: {
    client: string;
    buyer: string;
  }): Observable<GenericResponseDto<GetAllBuyersResponseDto[]>> {
    const params = new HttpParams({ fromObject: { ...request } });
    return this._http.get<GenericResponseDto<GetAllBuyersResponseDto[]>>(
      `${this.taskManagementBaseURL}/GetAllBuyers`,
      { params }
    );
  }

  getAllBuyersPaginated(request: {
    buyer?: string;
    client?: string;
    skip: number;
    take: number;
  }): Observable<GenericResponseDto<GetAllBuyersResponseDto[]>> {
    return this._http.post<GenericResponseDto<GetAllBuyersResponseDto[]>>(
      `${this.taskManagementBaseURL}/GetAllBuyersPaginated`,
      request
    );
  }

  MarkAsArchive(payload: {
    taskId: number;
    archive: boolean;
  }): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.taskManagementBaseURL + `/MarkAsArchive`,
      payload
    );
  }

  CopyTask(
    taskId: number
  ): Observable<GenericResponseDto<getTaskDetailResponse>> {
    return this._http.get<GenericResponseDto<getTaskDetailResponse>>(
      this.taskManagementBaseURL + `/CopyTask?taskId=${taskId}`
    );
  }

  MarkAsBulkArchive(payload: {
    taskIds: number[];
  }): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      `${this.taskManagementBaseURL}/MarkAsBulkArchive`,
      payload
    );
  }

  /**
   * Get sales orders list by client or order number for Sales Task lookup
   * @param customerIdD365 - Customer ID in D365
   * @param salesOrderIdD365 - Sales Order ID in D365
   * @returns Observable of sales order lookup results
   */
  getSalesOrdersListByClientOrOrder(
    customerIdD365 = "",
    salesOrderIdD365 = ""
  ): Observable<GenericResponseDto<SalesOrderLookupDto[]>> {
    const params = new HttpParams()
      .set("customerIdD365", customerIdD365)
      .set("salesOrderIdD365", salesOrderIdD365);
    return this._http.get<GenericResponseDto<SalesOrderLookupDto[]>>(
      `${environment.baseUrl}/SalesOrderManagement/GetAllSalesOrdersListByClientOrOrder`,
      { params }
    );
  }
}
